'use client';

import { Separator } from '@yaradigitallabs/ahua-react';
import type React from 'react';
import ProductionLineTable from './table';
import DateFilter from './date-filter';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  selectFacilitiesStatusData,
  selectFacilitiesStatusLoading,
  selectFacilitiesStatusError,
} from '../store/facility.selector';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFacilitiesStatus } from '../store/facility.thunk';
import {
  dateToUTCISOString,
  formatDateInputValue,
  replacePlaceholders,
} from '@/utils/helpers';
import { getEarlierDate, shiftDateByDays } from '../utils/helpers';
import routes from '@/app/routes';
import type { ProductionLine } from '../utils/types';

const ProductionLineListWrapperHeader: React.FC = () => {
  return (
    <div className="flex justify-between items-center px-8 py-4">
      <div className="header">
        <h1 className="text-lg font-bold">Production lines</h1>
        <p className="text-sm text-gray-400">
          Select date and production line to start reporting
        </p>
      </div>
    </div>
  );
};

const ProductionLineListWrapper: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const todayDate = formatDateInputValue(new Date());
  const [selectedDate, setSelectedDate] = useState<string>(
    searchParams.get('date') ?? shiftDateByDays(todayDate, -1)
  );

  const facilitiesData = useAppSelector(selectFacilitiesStatusData);
  const isLoading = useAppSelector(selectFacilitiesStatusLoading);
  const error = useAppSelector(selectFacilitiesStatusError);

  useEffect(() => {
    dispatch(
      getFacilitiesStatus({
        date: dateToUTCISOString(selectedDate),
      })
    );
  }, [dispatch, selectedDate]);

  const handleDateChange = (value: string) => {
    if (!value) {
      setSelectedDate(todayDate);
      return;
    }
    setSelectedDate(value);
  };

  const handleNextDate = () => {
    setSelectedDate(prevDate =>
      getEarlierDate(shiftDateByDays(prevDate, 1), todayDate)
    );
  };

  const handlePreviousDate = () => {
    setSelectedDate(prevDate => shiftDateByDays(prevDate, -1));
  };

  const handleSelectProductionLine = (line: ProductionLine) => {
    const lineId = line.id;
    const searchParams = new URLSearchParams({
      date: selectedDate,
    });

    navigate(
      `${replacePlaceholders(routes.PRODUCTION_REPORTING_ADD_PERIODS, { lineId })}?${searchParams.toString()}`
    );
  };

  return (
    <div className="border border-gray-300 rounded-lg bg-white w-full">
      <ProductionLineListWrapperHeader />
      <Separator />
      <DateFilter
        selectedDate={selectedDate}
        maxDate={todayDate}
        isNextDateDisabled={selectedDate >= todayDate}
        onDateChange={handleDateChange}
        onPreviousDate={handlePreviousDate}
        onNextDate={handleNextDate}
      />
      <Separator />
      <ProductionLineTable
        items={facilitiesData || []}
        isLoading={isLoading}
        error={error}
        handleSelectProductionLine={handleSelectProductionLine}
      />
    </div>
  );
};

export default ProductionLineListWrapper;
